(function () {
  'use strict';

  var CLONES = 2;

  class SaWhyChoosePlaud extends HTMLElement {
    constructor() {
      super();
      this.swipers = [];
      this.loopInfo = [];
      this.tabs = [];
      this.panels = [];
      this.activeIndex = 0;
      this.inView = false;
      this._swiperPoll = null;
      this._io = null;
      this._timer = null;
      this.mode = 'multi';
      this.single = null;
      this.singleInfo = null;
      this.stage = null;
      this._arrows = [];
      this._onTabClick = this._onTabClick.bind(this);
      this._onTabKeydown = this._onTabKeydown.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._updateTabOverflow = this._updateTabOverflow.bind(this);
      this._onArrowPressStart = this._onArrowPressStart.bind(this);
      this._onArrowPressEnd = this._onArrowPressEnd.bind(this);
    }

    connectedCallback() {
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.mode = this.dataset.mode === 'single' ? 'single' : 'multi';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 4000;
      this.gapPc = parseInt(this.dataset.gapPc, 10) || 32;
      this.gapMb = parseInt(this.dataset.gapMb, 10) || 16;

      this.style.setProperty('--sa-wc-autoplay', this.autoplaySpeed + 'ms');
      if (!this.autoplayEnabled) this.classList.add('no-autoplay');

      this.tabs = Array.from(this.querySelectorAll('.sa-why-choose__tab'));
      this.panels = Array.from(this.querySelectorAll('.sa-why-choose__panel'));
      if (!this.panels.length) return;

      this.tabbar = this.querySelector('.sa-why-choose__tabbar');
      this.tabsScroller = this.querySelector('.sa-why-choose__tabs');

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this._onTabClick);
        tab.addEventListener('keydown', this._onTabKeydown);
      });

      this._bindArrowPress();

      if (this.tabsScroller) {
        this.tabsScroller.addEventListener('scroll', this._updateTabOverflow, { passive: true });
      }
      window.addEventListener('resize', this._updateTabOverflow);
      this._updateTabOverflow();

      document.addEventListener('visibilitychange', this._onVisibility);

      this._whenSwiperReady(() => {
        if (this.mode === 'single') this._initSingle();
        else this._initSwipers();
      });

      if (this.designMode) {
        this._onBlockSelect = (e) => {
          const t = e.target;
          if (this.mode === 'single') {
            let idx = -1;
            if (t && t.classList && t.classList.contains('sa-why-choose__tab')) {
              idx = this.tabs.indexOf(t);
            } else if (t && t.closest) {
              const slide = t.closest('.sa-why-choose__slide');
              if (slide && slide.dataset.tabIndex != null) idx = parseInt(slide.dataset.tabIndex, 10);
            }
            if (idx > -1) this.activateTab(idx);
            return;
          }
          const panel = t && t.closest('.sa-why-choose__panel');
          if (panel) {
            const idx = this.panels.indexOf(panel);
            if (idx > -1) this.activateTab(idx);
          } else if (t && t.classList.contains('sa-why-choose__tab')) {
            const idx = this.tabs.indexOf(t);
            if (idx > -1) this.activateTab(idx);
          }
        };
        document.addEventListener('shopify:block:select', this._onBlockSelect);
      }
    }

    _whenSwiperReady(cb) {
      if (window.Swiper) {
        cb();
        return;
      }
      let tries = 0;
      this._swiperPoll = setInterval(() => {
        if (window.Swiper) {
          clearInterval(this._swiperPoll);
          this._swiperPoll = null;
          cb();
        } else if (++tries > 120) {
          clearInterval(this._swiperPoll);
          this._swiperPoll = null;
        }
      }, 50);
    }

    _initSwipers() {
      const self = this;

      this.panels.forEach((panel, index) => {
        const el = panel.querySelector('.sa-why-choose__swiper');
        if (!el) {
          this.swipers[index] = null;
          return;
        }
        const wrapper = el.querySelector('.swiper-wrapper');
        const realSlides = wrapper
          ? Array.from(wrapper.children).filter((s) => !s.classList.contains('sa-wc-clone'))
          : [];
        const R = realSlides.length;
        const prevEl = panel.querySelector('.sa-why-choose__arrow--prev');
        const nextEl = panel.querySelector('.sa-why-choose__arrow--next');

        let clones = 0;
        if (R >= 2 && wrapper && !wrapper.querySelector('.sa-wc-clone')) {
          clones = Math.min(R, CLONES);
          for (let i = clones - 1; i >= 0; i--) {
            const c = realSlides[R - clones + i].cloneNode(true);
            c.classList.add('sa-wc-clone');
            c.setAttribute('aria-hidden', 'true');
            wrapper.prepend(c);
          }
          for (let i = 0; i < clones; i++) {
            const c = realSlides[i].cloneNode(true);
            c.classList.add('sa-wc-clone');
            c.setAttribute('aria-hidden', 'true');
            wrapper.append(c);
          }
        }
        this.loopInfo[index] = { clones: clones, real: R, lastReal: -1, teleporting: false };

        const swiper = new window.Swiper(el, {
          slidesPerView: 'auto',
          centeredSlides: false,
          spaceBetween: this.gapMb,
          loop: false,
          initialSlide: clones,
          watchSlidesProgress: true,
          speed: 500,
          autoplay: false,
          navigation: { prevEl: prevEl, nextEl: nextEl },
          breakpoints: {
            0: { spaceBetween: this.gapMb, centeredSlides: false },
            1025: { spaceBetween: this.gapPc, centeredSlides: true },
          },
          on: {
            init() {
              self._sync(index);
            },
            slideChange() {
              self._sync(index);
            },
            transitionEnd() {
              self._teleport(index);
            },
          },
        });

        this.swipers[index] = swiper;
      });

      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;
            if (entry.isIntersecting) {
              this._startActive();
            } else {
              this._stopAll();
            }
          });
        },
        { rootMargin: '0px 0px -50px 0px' }
      );
      this._io.observe(this);
    }

    _initSingle() {
      const self = this;
      this.stage = this.querySelector('.sa-why-choose__stage');
      const el = this.stage ? this.stage.querySelector('.sa-why-choose__swiper') : null;
      if (!el) return;
      const wrapper = el.querySelector('.swiper-wrapper');
      const realSlides = wrapper
        ? Array.from(wrapper.children).filter((s) => !s.classList.contains('sa-wc-clone'))
        : [];
      const R = realSlides.length;
      const prevEl = this.stage.querySelector('.sa-why-choose__arrow--prev');
      const nextEl = this.stage.querySelector('.sa-why-choose__arrow--next');

      let clones = 0;
      if (R >= 2 && wrapper && !wrapper.querySelector('.sa-wc-clone')) {
        clones = Math.min(R, CLONES);
        for (let i = clones - 1; i >= 0; i--) {
          const c = realSlides[R - clones + i].cloneNode(true);
          c.classList.add('sa-wc-clone');
          c.setAttribute('aria-hidden', 'true');
          wrapper.prepend(c);
        }
        for (let i = 0; i < clones; i++) {
          const c = realSlides[i].cloneNode(true);
          c.classList.add('sa-wc-clone');
          c.setAttribute('aria-hidden', 'true');
          wrapper.append(c);
        }
      }
      this.singleInfo = { clones: clones, real: R, lastReal: -1, teleporting: false };

      this.single = new window.Swiper(el, {
        slidesPerView: 'auto',
        centeredSlides: false,
        spaceBetween: this.gapMb,
        loop: false,
        initialSlide: clones,
        watchSlidesProgress: true,
        speed: 500,
        autoplay: false,
        navigation: { prevEl: prevEl, nextEl: nextEl },
        breakpoints: {
          0: { spaceBetween: this.gapMb, centeredSlides: false },
          1025: { spaceBetween: this.gapPc, centeredSlides: true },
        },
        on: {
          init() {
            self._syncSingle();
          },
          slideChange() {
            self._syncSingle();
          },
          transitionEnd() {
            self._teleportSingle();
          },
        },
      });

      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;
            if (entry.isIntersecting) {
              this._startActive();
            } else {
              this._stopAll();
            }
          });
        },
        { rootMargin: '0px 0px -50px 0px' }
      );
      this._io.observe(this);
    }

    _syncSingle() {
      const swiper = this.single;
      const info = this.singleInfo;
      if (!swiper || !info || !this.stage) return;
      const R = info.real || 1;
      const real = info.clones
        ? (((swiper.activeIndex - info.clones) % R) + R) % R
        : swiper.activeIndex || 0;

      this.activeIndex = real;
      this._setTabStates(real);

      const dots = this.stage.querySelectorAll('.sa-why-choose__dot');
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === real);
        dot.classList.toggle('is-done', i < real);
        if (i !== real) dot.classList.remove('is-animating');
      });

      if (real !== info.lastReal) {
        if (this.inView && this.autoplayEnabled) this._restartFill(this.stage);
        this._scrollTabIntoView(this.tabs[real]);
      }
      info.lastReal = real;
    }

    _teleportSingle() {
      const swiper = this.single;
      const info = this.singleInfo;
      if (!swiper || !info || !info.clones || info.teleporting || swiper.destroyed) return;
      const ai = swiper.activeIndex;
      let target = null;
      if (ai >= info.real + info.clones) target = ai - info.real;
      else if (ai < info.clones) target = ai + info.real;
      if (target !== null) {
        info.teleporting = true;
        const el = swiper.el;
        if (el) el.classList.add('sa-wc-instant');
        swiper.slideTo(target, 0, false);
        if (el) {
          void el.offsetWidth;
          el.classList.remove('sa-wc-instant');
        }
        info.teleporting = false;
      }
    }

    _activeSwiper() {
      return this.mode === 'single' ? this.single : this.swipers[this.activeIndex];
    }

    _activeInfo() {
      return this.mode === 'single' ? this.singleInfo : this.loopInfo[this.activeIndex];
    }

    _activeHost() {
      return this.mode === 'single' ? this.stage : this.panels[this.activeIndex];
    }

    _setTabStates(index) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
      });
    }

    _teleport(index) {
      const swiper = this.swipers[index];
      const info = this.loopInfo[index];
      if (!swiper || !info || !info.clones || info.teleporting) return;
      if (swiper.destroyed) return;
      const ai = swiper.activeIndex;
      let target = null;
      if (ai >= info.real + info.clones) target = ai - info.real;
      else if (ai < info.clones) target = ai + info.real;
      if (target !== null) {
        info.teleporting = true;
        const el = swiper.el;
        if (el) el.classList.add('sa-wc-instant');
        swiper.slideTo(target, 0, false);
        if (el) {
          void el.offsetWidth;
          el.classList.remove('sa-wc-instant');
        }
        info.teleporting = false;
      }
    }

    _sync(index) {
      const swiper = this.swipers[index];
      const panel = this.panels[index];
      const info = this.loopInfo[index];
      if (!swiper || !panel || !info) return;
      const R = info.real || 1;
      const real = info.clones
        ? (((swiper.activeIndex - info.clones) % R) + R) % R
        : swiper.activeIndex || 0;

      const dots = panel.querySelectorAll('.sa-why-choose__dot');
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === real);
        dot.classList.toggle('is-done', i < real);
        if (i !== real) dot.classList.remove('is-animating');
      });

      if (
        real !== info.lastReal &&
        index === this.activeIndex &&
        this.inView &&
        this.autoplayEnabled
      ) {
        this._restartFill(panel);
      }
      info.lastReal = real;
    }

    _restartFill(panel) {
      const activeDot = panel.querySelector('.sa-why-choose__dot.is-active');
      if (!activeDot) return;
      activeDot.classList.remove('is-animating');
      void activeDot.offsetWidth;
      activeDot.classList.add('is-animating');
    }

    _startTimer() {
      this._stopTimer();
      if (!this.autoplayEnabled || !this.inView) return;
      const swiper = this._activeSwiper();
      const info = this._activeInfo();
      if (!swiper || swiper.destroyed || !info || info.real <= 1) return;
      this._timer = setInterval(() => {
        const sw = this._activeSwiper();
        if (sw && !sw.destroyed) sw.slideNext();
      }, this.autoplaySpeed);
    }

    _stopTimer() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    }

    _startActive() {
      const host = this._activeHost();
      if (this.autoplayEnabled && this.inView) {
        this._startTimer();
        if (host) this._restartFill(host);
      }
    }

    _stopAll() {
      this._stopTimer();
      const hosts = this.mode === 'single' ? [this.stage] : this.panels;
      hosts.forEach((host) => {
        const activeDot = host && host.querySelector('.sa-why-choose__dot.is-animating');
        if (activeDot) activeDot.classList.remove('is-animating');
      });
    }

    activateTab(index) {
      if (this.mode === 'single') {
        if (index === this.activeIndex || !this.tabs[index]) return;
        this._stopTimer();
        this.activeIndex = index;
        this._setTabStates(index);
        this._scrollTabIntoView(this.tabs[index]);
        if (this.single && this.singleInfo) {
          this.single.slideTo(index + this.singleInfo.clones);
        }
        if (this.inView) this._startActive();
        return;
      }

      if (index === this.activeIndex || !this.panels[index]) return;

      this._stopTimer();

      this.activeIndex = index;

      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
      });

      this.panels.forEach((panel, i) => {
        const on = i === index;
        panel.classList.toggle('is-active', on);
        if (on) {
          panel.removeAttribute('aria-hidden');
        } else {
          panel.setAttribute('aria-hidden', 'true');
        }
      });

      this._scrollTabIntoView(this.tabs[index]);

      const swiper = this.swipers[index];
      if (swiper) {
        swiper.update();
        this._sync(index);
      }
      if (this.inView) this._startActive();
    }

    _updateTabOverflow() {
      const sc = this.tabsScroller;
      if (!sc || !this.tabbar) return;
      const overflowing = sc.scrollWidth - sc.clientWidth > 1;
      const atStart = sc.scrollLeft <= 1;
      const atEnd = sc.scrollLeft + sc.clientWidth >= sc.scrollWidth - 1;
      this.tabbar.classList.toggle('can-scroll-left', overflowing && !atStart);
      this.tabbar.classList.toggle('can-scroll-right', overflowing && !atEnd);
    }

    _scrollTabIntoView(tab) {
      const sc = this.tabsScroller;
      if (!sc || !tab) return;
      const left = tab.offsetLeft - (sc.clientWidth - tab.offsetWidth) / 2;
      const max = sc.scrollWidth - sc.clientWidth;
      sc.scrollTo({ left: Math.max(0, Math.min(left, max)), behavior: 'smooth' });
    }

    _bindArrowPress() {
      this._arrows = Array.from(this.querySelectorAll('.sa-why-choose__arrow'));
      this._arrows.forEach((btn) => {
        btn.addEventListener('pointerdown', this._onArrowPressStart);
        btn.addEventListener('pointerup', this._onArrowPressEnd);
        btn.addEventListener('pointerleave', this._onArrowPressEnd);
        btn.addEventListener('pointercancel', this._onArrowPressEnd);
      });
    }

    _onArrowPressStart(e) {
      const btn = e.currentTarget;
      if (btn._pressTimer) {
        clearTimeout(btn._pressTimer);
        btn._pressTimer = null;
      }
      btn.classList.add('is-pressed');
    }

    _onArrowPressEnd(e) {
      const btn = e.currentTarget;
      if (btn._pressTimer) clearTimeout(btn._pressTimer);
      btn._pressTimer = setTimeout(() => {
        btn.classList.remove('is-pressed');
        btn._pressTimer = null;
      }, 260);
    }

    _onTabClick(e) {
      const idx = this.tabs.indexOf(e.currentTarget);
      if (idx > -1) this.activateTab(idx);
    }

    _onTabKeydown(e) {
      const current = this.tabs.indexOf(e.currentTarget);
      if (current < 0) return;
      let next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        next = (current + 1) % this.tabs.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        next = (current - 1 + this.tabs.length) % this.tabs.length;
      } else if (e.key === 'Home') {
        next = 0;
      } else if (e.key === 'End') {
        next = this.tabs.length - 1;
      }
      if (next === null) return;
      e.preventDefault();
      this.activateTab(next);
      this.tabs[next].focus();
    }

    _onVisibility() {
      if (document.hidden) {
        this._stopTimer();
      } else if (this.inView) {
        this._startActive();
      }
    }

    disconnectedCallback() {
      this._stopTimer();
      if (this._swiperPoll) {
        clearInterval(this._swiperPoll);
        this._swiperPoll = null;
      }
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this._onTabClick);
        tab.removeEventListener('keydown', this._onTabKeydown);
      });
      this._arrows.forEach((btn) => {
        btn.removeEventListener('pointerdown', this._onArrowPressStart);
        btn.removeEventListener('pointerup', this._onArrowPressEnd);
        btn.removeEventListener('pointerleave', this._onArrowPressEnd);
        btn.removeEventListener('pointercancel', this._onArrowPressEnd);
        if (btn._pressTimer) {
          clearTimeout(btn._pressTimer);
          btn._pressTimer = null;
        }
      });
      this._arrows = [];
      if (this.tabsScroller) {
        this.tabsScroller.removeEventListener('scroll', this._updateTabOverflow);
      }
      window.removeEventListener('resize', this._updateTabOverflow);
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._onBlockSelect) {
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        this._onBlockSelect = null;
      }
      this.swipers.forEach((swiper) => {
        if (swiper && typeof swiper.destroy === 'function' && !swiper.destroyed) {
          swiper.destroy(true, true);
        }
      });
      this.swipers = [];
      if (this.single && typeof this.single.destroy === 'function' && !this.single.destroyed) {
        this.single.destroy(true, true);
      }
      this.single = null;
    }
  }

  if (!customElements.get('sa-why-choose-plaud')) {
    customElements.define('sa-why-choose-plaud', SaWhyChoosePlaud);
  }
})();
